import {addDate} from '@shared/dateUtils';
import {formatNumberWithCommas} from '@di-shared/utils';
import {ERROR, FALLBACK} from '@di-shared/const';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import clsx from 'clsx';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import useHistoryLock from '@di-shared/features/history/setting/useHistoryLock';
import useDrivingInfoKr from '@di-shared/features/history/driving-info/useDrivingInfo.kr';
import {calculateRelativePercent} from '@di-shared/utils/graph';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {emitEventLog} from '@di-shared/features/analytics';

const DRIVING_DISTANCE_GRAPH = {
  PERCENT: 80,
};

export default function DrivingDistanceSectionKr({vin}: {vin: string}) {
  const {isHistoryUnlock} = useHistoryLock({vin});
  const {isSameCcsAgreeMonth, isCurrentDrivingDistanceNone} = useDrivingInfoKr({vin});

  // 주행기록 잠금
  if (!isHistoryUnlock) {
    return <DrivingDistanceSectionLock vin={vin} />;
  }

  // CCS 동의 당월
  if (isSameCcsAgreeMonth) {
    return <DrivingDistanceSectionAgree vin={vin} />;
  }

  // 당월 주행없음
  if (isCurrentDrivingDistanceNone) {
    return <DrivingDistanceSectionNone vin={vin} />;
  }

  // 당월 주행
  return <DrivingDistanceSectionDefault vin={vin} />;
}

// FALLBACK(LOADING, ERROR) UI
DrivingDistanceSectionKr.Error = () => <DrivingDistanceSectionFallback fallback={FALLBACK.ERROR} />;
DrivingDistanceSectionKr.Loading = () => <DrivingDistanceSectionFallback fallback={FALLBACK.LOADING} />;

function DrivingDistanceSectionFallback({fallback}: {fallback: FALLBACK}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();
  const {defaultDistanceSymbol} = useUnitConverter();

  return (
    <div className={clsx('section_container trip_month_distance', {skeleton: fallback === FALLBACK.LOADING})}>
      <div className="section_container_header">
        <h3 className="body_medium_b">{t('T10026_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}</h3>
      </div>
      <div className="trip_month type_error">
        <div className="trip_month_info">
          <span className="trip_month_value">{ERROR.DASH}</span>
          <span className="trip_month_unit">{defaultDistanceSymbol}</span>
        </div>
        <span className="trip_month_compare">
          <span className="trip_month_value">{ERROR.DASH}</span>
          <span className="trip_month_unit">{defaultDistanceSymbol}</span>
        </span>
        <p className="trip_month_desc"></p>
        <div className="trip_month_graph_area">
          <div className="distance_graph_wrap">
            <div className="bar_graph type_horizon">
              <div className="bar_graph_item">
                <span
                  className="graph_bar"
                  style={{width: `${DRIVING_DISTANCE_GRAPH.PERCENT}%`}}
                />
              </div>
              <span className="graph_title">{CldrDatetimeFormatter.format(today, 'MMM')}</span>
              <div className="bar_graph_item">
                <span
                  className="graph_bar"
                  style={{width: `${DRIVING_DISTANCE_GRAPH.PERCENT}%`}}
                />
              </div>
              <span className="graph_title">{t('T20089')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DrivingDistanceSectionLock({vin}: {vin: string}) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {isHistoryUnlock} = useHistoryLock({vin});

  if (isHistoryUnlock === null) {
    return <DrivingDistanceSectionKr.Loading />;
  }

  return (
    <div className="section_container trip_month_distance">
      <LinkTo
        to={ROUTE_PATHS.HISTORY.getFullPath(vin)}
        className="section_container_full_btn"
        aria-label={t('T20092')}
        onClick={() =>
          emitEventLog({
            event: 'di_driveinfo_interaction',
            data_action: 'view banner',
            data_name: t('T10026_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')}),
          })
        }
      />
      <div className="section_container_header">
        <h3 className="body_medium_b">{t('T10026_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}</h3>
      </div>
      <div className="trip_month type_error">
        <p
          className="trip_month_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T10191')}
        </p>
        <div className="trip_month_graph_area">
          <div className="distance_graph_wrap">
            <div className="bar_graph type_horizon">
              <div className="bar_graph_item">
                <span
                  className="graph_bar"
                  style={{width: `${DRIVING_DISTANCE_GRAPH.PERCENT}%`}}
                />
              </div>
              <span className="graph_title">{CldrDatetimeFormatter.format(today, 'MMM')}</span>
              <div className="bar_graph_item">
                <span
                  className="graph_bar"
                  style={{width: `${DRIVING_DISTANCE_GRAPH.PERCENT}%`}}
                />
              </div>
              <span className="graph_title">{t('T20089')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DrivingDistanceSectionAgree({vin}: {vin: string}) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {currentDrivingDistance} = useDrivingInfoKr({vin});
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();

  return (
    <div className="section_container trip_month_distance">
      <LinkTo
        to={ROUTE_PATHS.HISTORY.getFullPath(vin)}
        className="section_container_full_btn"
        aria-label={t('T20092')}
        onClick={() =>
          emitEventLog({
            event: 'di_driveinfo_interaction',
            data_action: 'view banner',
            data_name: t('T10026_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')}),
          })
        }
      />
      <div className="section_container_header">
        <h3 className="body_medium_b">{t('T10026_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}</h3>
      </div>
      <div className="trip_month">
        <div className="trip_month_info">
          <span className="trip_month_value">{formatNumberWithCommas(convertDistanceValue(currentDrivingDistance.totalDistance))}</span>
          <span className="trip_month_unit">{convertDistanceSymbol(currentDrivingDistance.totalDistance)}</span>
        </div>
        <p className="trip_month_desc">{t('T10164_$month', {month: CldrDatetimeFormatter.format(addDate(today, {months: 1}), 'MMM')})}</p>
        <div className="trip_month_graph_area">
          <div className="distance_graph_wrap">
            <div className="bar_graph type_horizon">
              <div className="bar_graph_item is-active">
                <span
                  className="graph_bar"
                  style={{width: `${DRIVING_DISTANCE_GRAPH.PERCENT}%`}}
                >
                  <span className="speech_bubble">{formatNumberWithCommas(currentDrivingDistance.totalDistance)}</span>
                </span>
              </div>
              <span className="graph_title">{CldrDatetimeFormatter.format(today, 'MMM')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DrivingDistanceSectionNone({vin}: {vin: string}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();

  return (
    <div className="section_container trip_month_distance">
      <LinkTo
        to={ROUTE_PATHS.HISTORY.getFullPath(vin)}
        className="section_container_full_btn"
        aria-label={t('T20092')}
        onClick={() =>
          emitEventLog({
            event: 'di_driveinfo_interaction',
            data_action: 'view banner',
            data_name: t('T10026_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')}),
          })
        }
      />
      <div className="section_container_header">
        <h3 className="body_medium_b">{t('T10026_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}</h3>
      </div>
      <div className="trip_month type_error">
        <p
          className="trip_month_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T10186')}
        </p>
        <div className="trip_month_graph_area">
          <div className="distance_graph_wrap">
            <div className="bar_graph type_horizon">
              <div className="bar_graph_item">
                <span
                  className="graph_bar"
                  style={{width: `${DRIVING_DISTANCE_GRAPH.PERCENT}%`}}
                />
              </div>
              <span className="graph_title">{CldrDatetimeFormatter.format(today, 'MMM')}</span>
              <div className="bar_graph_item">
                <span
                  className="graph_bar"
                  style={{width: `${DRIVING_DISTANCE_GRAPH.PERCENT}%`}}
                />
              </div>
              <span className="graph_title">{t('T20089')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DrivingDistanceSectionDefault({vin}: {vin: string}) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {drivingData, currentDrivingDistance} = useDrivingInfoKr({vin});
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  const averageDrivingDistanceCompare = currentDrivingDistance.totalDistance - drivingData.avgDistance;
  const drivingDistanceGraphPercentage = calculateRelativePercent({
    value1: currentDrivingDistance.totalDistance,
    value2: drivingData.avgDistance,
    percent: DRIVING_DISTANCE_GRAPH.PERCENT,
  });
  return (
    <div className="section_container trip_month_distance">
      <LinkTo
        to={ROUTE_PATHS.HISTORY.getFullPath(vin)}
        className="section_container_full_btn"
        aria-label={t('T20092')}
        onClick={() =>
          emitEventLog({
            event: 'di_driveinfo_interaction',
            data_action: 'view banner',
            data_name: t('T10026_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')}),
          })
        }
      />
      <div className="section_container_header">
        <h3 className="body_medium_b">{t('T10026_$month', {month: CldrDatetimeFormatter.format(today, 'MMM')})}</h3>
      </div>
      <div className="trip_month">
        <div className="trip_month_info">
          <span className="trip_month_value">{formatNumberWithCommas(convertDistanceValue(currentDrivingDistance.totalDistance))}</span>
          <span className="trip_month_unit">{convertDistanceSymbol(currentDrivingDistance.totalDistance)}</span>
        </div>

        <span className="trip_month_compare">
          <span
            className={clsx('ic', {
              ic_di_count_up: averageDrivingDistanceCompare >= 0,
              ic_di_count_down: averageDrivingDistanceCompare < 0,
            })}
            aria-hidden="true"
          >
            <span className="blind">{averageDrivingDistanceCompare >= 0 ? t('T10059') : t('T10058')}</span>
          </span>
          <span className="trip_month_value">{formatNumberWithCommas(convertDistanceValue(Math.abs(averageDrivingDistanceCompare)))}</span>
          <span className="trip_month_unit">{convertDistanceSymbol(averageDrivingDistanceCompare)}</span>
        </span>

        <p className="trip_month_desc">
          {averageDrivingDistanceCompare === 0 ? t('T20107') : averageDrivingDistanceCompare > 0 ? t('T20054') : t('T20055')}
        </p>

        <div className="trip_month_graph_area">
          <div className="distance_graph_wrap">
            <div className="bar_graph type_horizon">
              <div className="bar_graph_item is-active">
                <span
                  className="graph_bar"
                  style={{
                    width: averageDrivingDistanceCompare >= 0 ? `${DRIVING_DISTANCE_GRAPH.PERCENT}%` : `${drivingDistanceGraphPercentage}%`,
                  }}
                >
                  <span className="speech_bubble">{formatNumberWithCommas(currentDrivingDistance.totalDistance)}</span>
                </span>
              </div>
              <span className="graph_title">{CldrDatetimeFormatter.format(today, 'MMM')}</span>

              <div className="bar_graph_item">
                <span
                  className="graph_bar"
                  style={{
                    width: averageDrivingDistanceCompare >= 0 ? `${drivingDistanceGraphPercentage}%` : `${DRIVING_DISTANCE_GRAPH.PERCENT}%`,
                  }}
                />
              </div>
              <span className="graph_title">{t('T20089')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
